/**
 * Executive Chat Component
 * 
 * Main chat interface for the Executive Co-Pilot.
 * Displays messages, handles input, and renders AI actions.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';

// Shared modules (centralized)
import { useThinkingPhrases, ActionButtonsRow, filterRenderableActions } from '../copilot/shared';
import CopilotMarkdown from '../copilot/CopilotMarkdown';

// Message Component - simplified without icons
const ChatMessage = ({ message, onAction }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const isUser = message.role === 'user';

  // Parse actions from message content (inline JSON actions)
  const parseMessageWithActions = (content, actions = []) => {
    // Safety check - ensure content is a string
    if (!content || typeof content !== 'string') {
      return { cleanContent: content || '', actions };
    }
    // Remove inline JSON action objects from the text
    let cleanContent = content;
    const inlineActionPattern = /\*\*\{"action":[^}]+\}\*\*/g;
    cleanContent = cleanContent.replace(inlineActionPattern, '');
    
    return { cleanContent, actions };
  };

  const { cleanContent } = parseMessageWithActions(
    message.content, 
    message.actions || []
  );
  
  // Filter out non-renderable actions (AWAIT_FEEDBACK, etc.)
  const renderableActions = filterRenderableActions(message.actions || []);

  return (
    <div className={cn(
      "flex py-3",
      isUser ? "justify-end" : "justify-start"
    )}>
      <div className={cn(
        "max-w-[80%] rounded-2xl px-4 py-3",
        isUser 
          ? "bg-indigo-600 text-white rounded-br-sm" 
          : "rounded-bl-sm bg-gray-100 dark:bg-gray-800/50"
      )}>
        {isUser ? (
          // User messages: simple text without markdown processing
          <p className="text-sm whitespace-pre-wrap m-0">{cleanContent}</p>
        ) : (
          // Assistant messages: full markdown rendering with tighter spacing
          <div className={cn(
            "text-sm leading-relaxed",
            "[&>p]:mb-2 [&>p:last-child]:mb-0",
            "[&>p:first-child]:mt-0",
            "[&>ul]:mb-2 [&>ul:last-child]:mb-0",
            "[&>ol]:mb-2 [&>ol:last-child]:mb-0",
            colors.textPrimary
          )}>
            <CopilotMarkdown 
              content={cleanContent?.trim()}
              enableEntityLinks={true}
            />
          </div>
        )}
        
        {/* Render actions using unified ActionButtonsRow */}
        {!isUser && renderableActions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <ActionButtonsRow actions={renderableActions} onAction={onAction} />
          </div>
        )}
      </div>
    </div>
  );
};

// Welcome State Component
const WelcomeState = ({ welcomeData, onSuggestedPrompt }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  return (
    <div className="flex flex-col items-center justify-center h-full px-4 py-8">
      <h2 className={cn("text-xl font-semibold mb-2 text-center", colors.textPrimary)}>
        Executive Intelligence
      </h2>
      
      <p className={cn("text-center mb-8 max-w-md", colors.textSecondary)}>
        {welcomeData?.welcome_message || "I'm ready to help you explore your products."}
      </p>
      
      <div className="w-full max-w-md space-y-2">
        <p className={cn("text-sm font-medium mb-3", colors.textMuted)}>Try asking:</p>
        {(welcomeData?.suggested_prompts || []).map((prompt, idx) => (
          <button
            key={idx}
            onClick={() => onSuggestedPrompt(prompt)}
            className={cn(
              "w-full text-left px-4 py-3 rounded-lg text-sm transition-colors",
              "border hover:border-indigo-300 dark:hover:border-indigo-700",
              colors.bgTertiary, colors.borderPrimary, colors.textSecondary,
              "hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
            )}
          >
            &ldquo;{prompt}&rdquo;
          </button>
        ))}
      </div>
    </div>
  );
};

// Main Executive Chat Component
const ExecutiveChat = ({
  messages,
  isLoading,
  welcomeData,
  onSendMessage,
  onAction,
  scope,
  scopeLabel,
  className,
  hideWelcome = false,
  showHeader = true
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Rotating thinking phrases
  const { phrase: thinkingPhrase, isVisible: phraseVisible } = useThinkingPhrases(isLoading);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle send
  const handleSend = useCallback(() => {
    if (!input.trim() || isLoading) return;
    onSendMessage(input.trim());
    setInput('');
  }, [input, isLoading, onSendMessage]);

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle suggested prompt click
  const handleSuggestedPrompt = (prompt) => {
    onSendMessage(prompt);
  };

  const hasMessages = messages && messages.length > 0;

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4">
        {!hasMessages && !hideWelcome ? (
          <WelcomeState 
            welcomeData={welcomeData} 
            onSuggestedPrompt={handleSuggestedPrompt}
          />
        ) : (
          <div className="py-4">
            {messages.map((msg, idx) => (
              <ChatMessage key={idx} message={msg} onAction={onAction} />
            ))}
            
            {/* Loading indicator with rotating thinking phrases */}
            {isLoading && (
              <div className="flex justify-start py-3" data-testid="executive-thinking-indicator">
                <div className={cn("rounded-lg px-3 py-2 flex items-center gap-2", colors.bgCard)}>
                  <Loader2 className={cn("w-4 h-4 animate-spin flex-shrink-0", colors.textMuted)} />
                  <span 
                    className={cn(
                      "text-sm italic transition-opacity duration-200",
                      colors.textMuted,
                      phraseVisible ? "opacity-100" : "opacity-0"
                    )}
                  >
                    {thinkingPhrase}
                  </span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area - no border, centered, 70% width */}
      <div className="p-4 shrink-0">
        <div className="flex gap-3 max-w-[70%] mx-auto">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about your products..."
            disabled={isLoading}
            rows={3}
            className={cn(
              "flex-1 resize-none rounded-xl px-4 py-3 text-sm",
              "border focus:outline-none focus:ring-2 focus:ring-indigo-500/50",
              "transition-all",
              colors.bgSecondary, colors.borderPrimary, colors.textPrimary,
              "placeholder:text-gray-400 dark:placeholder:text-gray-500",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          />
          <div className="flex flex-col justify-end">
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="icon"
              className={cn(
                "h-12 w-12 rounded-xl shrink-0",
                "bg-indigo-600 hover:bg-indigo-700 text-white",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
        <p className={cn("text-xs mt-2 text-center", colors.textMuted)}>
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default ExecutiveChat;
